import Data from 'app-core/Data';
import Auth from 'src/auth.js';
import Vue from 'vue';
import signalR from 'jqueryless-signalr';
import config from 'app-core/config';
import signalrRequestCreativeAccess from 'view/creative/signalr-request-creative-access';
import moment from 'moment';

function initSignalR() {
	if (__DEBUG__)
		console.debug('Init SignalR');

	let signalRConnection = null;
	Data
		.callMethod('GetExternalPublicApiUrl')
		.then((url) => {
			if (__DEBUG__) console.log('ExternalPublicApiUrl: ', url);
			signalRConnection = signalR(
				`${url.toLowerCase().replace('/api/v1/', '').replace('/api/v1', '')}/signalr`,
				{ useDefaultPath: false }
			);
			signalRConnection.qs = { 'access_token': Auth.accessToken() };
			signalRConnection
				.error((err) => {
					if (__DEBUG__) console.log('signalRConnection Error:', { err });
					setTimeout(() => {
						signalRConnection.start();
					}, 5000);
				});
			signalRConnection
				.disconnected(() => {
					setTimeout(() => {
						signalRConnection.start();
					}, 5000);
				});

			const vueClientHub = signalRConnection.createHubProxy('vueClientHub');
			vueClientHub
				.on('onNotification', (eventName, data) => {
					if (__DEBUG__)
						console.log(`${moment().format('mm:ss.SSS')} signalR onNotification event: ${eventName}`, data);

					if (eventName === 'CreativePublished') processCreativePublished(eventName, data);
					else if (eventName === 'InvoiceStatusChanged') processInvoiceStatusChanged(eventName, data);
					else if (eventName === 'CreativeGenerationStatusChanged') processCreativeGenerationStatusChanged(eventName, data);
					else if (eventName === 'ExternalServiceDefinition.Changed.LifeStatus') processExternalServiceLifeStatus(eventName, data);
					else if (eventName === 'PersistentJobEntity.StatusChanged') processPersistentJobEntityStatusChanged(eventName, data);
					else signalrRequestCreativeAccess.processRequestCreativeAccess(eventName, data);

					config.eventsBus.$emit('signalr-on-notification', { eventName, data });
				});

			signalRConnection
				.start()
				.done(() => {
					if (__DEBUG__) console.log('signalRConnection Done');
				})
				.fail(() => {
					if (__DEBUG__) console.log('signalRConnection Fail');
				});

			Vue.prototype.$signalRConnection = signalRConnection;
			Vue.prototype.$vueClientHub = vueClientHub;
		});
}

async function processExternalServiceLifeStatus(eventName, data) {
	const service = await Data.getExternalService(data?.Oid);
	if (service) {
		service.LifeStatus = data?.LifeStatus;
		service.TextStatus = data?.TextStatus;
	}
}

function processCreativePublished(eventName, data) {
	let app = config.getVueApp();

	const status = data?.Status;
	const creativeName = data?.Name;
	const hasWarnings = data.HasWarnings;

	const successMessage = app.$tCaption('The final audio for creative (CREATIVE_NAME) published.', 'creative.Detail').replace('CREATIVE_NAME', creativeName);
	const errorMessage = app.$tCaption('The publication of the creative (CREATIVE_NAME) finished with errors.', 'creative.Detail').replace('CREATIVE_NAME', creativeName);
	const cancelMessage = app.$tCaption('The publication of the creative (CREATIVE_NAME) canceled.', 'creative.Detail').replace('CREATIVE_NAME', creativeName);
	const warningMessage = app.$tCaption('The publication of the creative (CREATIVE_NAME) finished with warnings.', 'creative.Detail').replace('CREATIVE_NAME', creativeName);

	const icon = hasWarnings ? 'exclamation-triangle' : (status === 1 ? 'check' : (status === 2 ? 'times' : 'times'));
	const type = hasWarnings ? 'warning' : (status === 1 ? 'success' : (status === 2 ? 'error' : 'info'));
	const message = hasWarnings ? warningMessage : (status === 1 ? successMessage : (status === 2 ? errorMessage : cancelMessage));

	config.notifyMessage(message, { icon, type });
}

function processInvoiceStatusChanged(eventName, data) {
	let app = config.getVueApp();

	const status = data?.ProductsActivationStatus;
	const templateName = data?.Description;

	const activatingMessage = app.$tCaption('The template "TEMPLATE_NAME" is being activated.', 'creative.CreativeTemplates').replace('TEMPLATE_NAME', templateName);
	const activatedMessage = app.$tCaption('The template "TEMPLATE_NAME" was successfully activated.', 'creative.CreativeTemplates').replace('TEMPLATE_NAME', templateName);
	const errorMessage = app.$tCaption('The activation of "TEMPLATE_NAME" has been finished with errors.', 'creative.Detail').replace('TEMPLATE_NAME', templateName);

	const icon = status === 4 ? 'times' : status === 3 ? 'check' : 'hourglass-start';
	const type = status === 4 ? 'error' : status === 3 ? 'info' : 'info';
	const message = status === 4 ? errorMessage : status === 3 ? activatedMessage : activatingMessage;

	if (status === 2) return;

	config.notifyMessage(message, { icon, type });
}

function processCreativeGenerationStatusChanged(eventName, data) {
	let app = config.getVueApp();

	if (app.$isAICreative()) return;

	if (data?.Id && data?.GenerationStatus >= 3) {
		if (!data.RootId) {
			app.$notifyMessage(app.$tCaption('Finished generating creative "<NAME>"').replace('<NAME>', data?.Name), { busy: false })

			if (config.tabsManager.instance.isInitiatedTab) {
				const message = data?.GenerationStatus === 3 ? 
					app.$tCaption('The creative "<NAME>" has been successfully created. Do you want to open it?') :
					app.$tCaption('The creative "<NAME>" has been generated with errors. Do you want to open it?');

				app.$confirmMessage({
					title: data?.GenerationStatus === 3 ? app.$tCaption('Creative generated', '@actions') : app.$tCaption('Error!', '@actions'),
					text: message.replace('<NAME>', data?.Name),
					handler: () => {
						app.$router.push({ path: `/creative/Detail/${data?.Id}` });
					}
				});
			}
		} else {
			app.$notifyMessage(app.$tCaption('Revision for creative "<NAME>" has been successfully created').replace('<NAME>', data?.Name), { busy: false });
		}
	}
}

function processPersistentJobEntityStatusChanged(eventName, data) {
	if (eventName === 'PersistentJobEntity.StatusChanged' && data?.Code === 'GenerationInsertions') {
		if (data?.Status > 2) {
			let app = config.getVueApp();
			if (data?.Error || data?.ErrorMessage) {
				app.$notifyMessage(`${data.Error || data.ErrorMessage} ${data.Url && `<br><br><a href="${data.Url}">${this.$tCaption('Download file')}</a>`}`, { type: 'error', fullScreen: true });
			} else {
				app.$notifyMessage(app.$tCaption('The sub-tracks have been processed.', '@creative'), { type: 'success' });
			}
		}
	}
	
}

function emitNotification(userIds, eventName, data) {
	return Data.postExternalApi('Service/EmitNoticications', [{
		UserIds: userIds || [],
		EventName: eventName,
		Data: data
	}]);
}

export {
	initSignalR,
	emitNotification
};

export default {
	initSignalR,
	emitNotification
};
